import React, { useReducer, useMemo } from "react";
import Dayjs from "dayjs";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";

// Custom.
import PreviewLayout from "containers/Layout/PreviewLayout";
import { Form, FormTabProps } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_COURSE_MOMENTS,
  GET_LANGUAGE_REPRESENTATIVE_EDUCATOR_DIPLOMA,
  UPDATE_LANGUAGE_REPRESENTATIVE_EDUCATOR_DIPLOMA,
  CREATE_LANGUAGE_REPRESENTATIVE_EDUCATOR_DIPLOMA,
} from "api";
import { SEARCH_USER } from "@vocollege/app/dist/modules/VoApi";
import { VoAuth } from "@vocollege/app";
import { reducer, getContactName } from "@vocollege/app/dist/modules/VoHelpers";
import { useStyles } from "ui/styles/form";
import LanguageRepresentativeEducatorDiplomaPreview from "containers/Templates/LanguageRepresentativeEducatorDiploma/LanguageRepresentativeEducatorDiplomaPreview";
import { FormProps } from "@vocollege/components/dist/Form";
import { getUserString } from "@vocollege/app/dist/modules/VoHelpers";
import BatchSelection from "components/BatchSelection";
import BatchPersonList, {
  BatchPersonListItem,
} from "components/BatchPersonList";
import { getPageTitle } from "services/Utils";
import SaveConsentDialog from "components/SaveConsentDialog";

const initialFormState = {
  formLoading: true,
  formState: {
    isDirty: false,
    isValid: false,
  },
  mode: "single",
};

const LanguageRepresentativeEducatorDiplomaForm: React.FC = () => {
  const initialDate = Dayjs().format("YYYY-MM-DD");
  const urlParams = useParams<any>();
  const { id } = urlParams;
  const classes = useStyles();
  const navigate = useNavigate();
  const [formState, formDispatch] = useReducer(reducer, initialFormState);

  // Methods.

  const getInitialState = () => {
    const initialState: { [key: string]: any } = {
      id: 0,
      firstname: "",
      lastname: "",
      date: initialDate,
      place: "Stockholm",
      leader1: null,
      leader2: null,
      author: null,
      done: 0,
      course_moment: null,
      list: [],
    };
    if (id && id === "skapa") {
      initialState.author = {
        id: VoAuth.currentUser.id,
        name: VoAuth.currentUser.name,
        type: "user",
      };
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(reducer, getInitialState());

  const isMultiple = () => {
    return formState.mode === "multiple";
  };

  const handleCancel = () => {
    navigate("/diplom-sprakombudsutbildare");
  };

  const handleComplete = (obj: any) => {
    if (isMultiple()) {
      navigate("/diplom-sprakombudsutbildare");
    } else {
      navigate(`/diplom-sprakombudsutbildare/${obj[Object.keys(obj)[0]].id}`);
    }
  };

  const handleFormChange: FormProps["onFormChange"] = (formState) => {
    formDispatch({ key: "formState", values: formState });
  };

  const handleBatchPersonListChange = (items: BatchPersonListItem[]) => {
    dispatch({
      values: {
        firstname: items[0].firstname,
        lastname: items[0].lastname,
        list: items,
      },
    });
  };

  const getBatchPersonListInitialData = useMemo(
    () =>
      state.course_moment &&
      state.course_moment.participants &&
      state.course_moment.participants.length > 0
        ? state.course_moment.participants
        : null,
    [state],
  );

  const getExtraActions = () => {
    return (
      isMultiple() && (
        <>
          <BatchPersonList
            className="vo-batch-person-list-button"
            onChange={handleBatchPersonListChange}
            fields={["firstname", "lastname"]}
            disableFields={Boolean(getBatchPersonListInitialData)}
            initialData={getBatchPersonListInitialData}
            content={
              getBatchPersonListInitialData && (
                <Alert severity="warning">
                  {
                    I18n.get.languageRepresentativeDiploma.messages
                      .participantsRetrievedFromCourseMoment
                  }
                </Alert>
              )
            }
          />
          <div className={classes.grow}></div>
        </>
      )
    );
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];
    tabs.push({
      key: "content",
      label: I18n.get.misc.content,
      fields: [
        {
          name: "list",
          label: "",
          type: "custom",
          grid: {
            xs: 12,
          },
          render: (data: any) => <></>,
          overrideValue: state.list,
        },
        {
          name: "date",
          label: I18n.get.misc.date,
          type: "date_field",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          onChange: (value: any) => dispatch({ key: "date", values: value }),
        },
        {
          name: "place",
          label: I18n.get.languageRepresentativeEducatorDiploma.labels.place,
          type: "text",
          grid: {
            xs: 12,
            md: 6,
          },
          onChange: (value: any) => dispatch({ key: "place", values: value }),
        },
        {
          name: "firstname",
          label: I18n.get.user.labels.firstname,
          type: !isMultiple() ? "text" : "hidden",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          validation: {
            required: !isMultiple(),
          },
          onChange: (value: any) =>
            dispatch({ key: "firstname", values: value }),
        },
        {
          name: "lastname",
          label: I18n.get.user.labels.lastname,
          type: !isMultiple() ? "text" : "hidden",
          required: true,
          validation: {
            required: !isMultiple(),
          },
          grid: {
            xs: 12,
            md: 6,
          },
          onChange: (value: any) =>
            dispatch({ key: "lastname", values: value }),
        },
        {
          name: "leader1",
          label: I18n.get.languageRepresentativeEducatorDiploma.labels.leader1,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          required: true,
          validation: {
            required: true,
          },
          params: {
            renderFieldValue: (item: any) => getUserString(item),
            dialog: {
              renderTitleField: (item: any) => getUserString(item),
              types: ["user"],
              query: SEARCH_USER,
              category: "searchUser",
            },
            fields: {
              user: {
                title: "name",
              },
            },
          },
          onChange: (value: any) => dispatch({ key: "leader1", values: value }),
        },
        {
          name: "leader2",
          label: I18n.get.languageRepresentativeEducatorDiploma.labels.leader2,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          params: {
            renderFieldValue: (item: any) => getUserString(item),
            dialog: {
              renderTitleField: (item: any) => getUserString(item),
              types: ["user"],
              query: SEARCH_USER,
              category: "searchUser",
            },
            fields: {
              user: {
                title: "name",
              },
            },
          },
          onChange: (value: any) => dispatch({ key: "leader2", values: value }),
        },
        {
          name: "author",
          label: I18n.get.misc.author,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          params: {
            dialog: {
              renderTitleField: (item: any) => getContactName(item),
              types: ["user"],
              query: SEARCH_USER,
              category: "searchUser",
            },
            fields: {
              user: {
                title: "name",
              },
            },
          },
        },
        {
          name: "course_moment",
          label: I18n.get.vocertCourseMoment.label,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          params: {
            renderFieldValue: (item: any) => item?.title || "",
            dialog: {
              types: ["course_moment"],
              renderTitleField: (item: any) => item?.title || "",
              query: GET_COURSE_MOMENTS,
              category: "certCourseMoments.data",
            },
          },
          onChange: (value: any) =>
            dispatch({ key: "course_moment", values: value }),
        },
      ],
    });
    return tabs;
  };

  return (
    <>
      <PreviewLayout
        className={classes.root}
        leftColumn={
          <>
            {!state.uid && !formState.formLoading && (
              <Alert severity="error" className={classes.row}>
                {I18n.get.diploma.messages.saveBeforePrint}
              </Alert>
            )}
            {isMultiple() && getBatchPersonListInitialData && (
              <Alert
                severity="warning"
                sx={(theme) => ({ marginBottom: theme.spacing(2) })}
              >
                {
                  I18n.get.languageRepresentativeDiploma.messages
                    .participantsRetrievedFromCourseMoment
                }
              </Alert>
            )}
            {!formState.formLoading && id && id === "skapa" && (
              <BatchSelection
                onChange={(tab) => formDispatch({ key: "mode", values: tab })}
              />
            )}
            <Form
              tabs={getTabs()}
              labels={{
                loading: I18n.get.form.labels.loadingContent,
                create:
                  I18n.get.languageRepresentativeEducatorDiploma.labels
                    .createLanguageRepresentativeEducatorDiploma,
                created: isMultiple()
                  ? I18n.get.languageRepresentativeEducatorDiploma.messages
                      .languageRepresentativeEducatorDiplomasGenerated
                  : I18n.get.languageRepresentativeEducatorDiploma.messages
                      .languageRepresentativeEducatorDiplomaSaved,
                updated:
                  I18n.get.languageRepresentativeEducatorDiploma.messages
                    .languageRepresentativeEducatorDiplomaSaved,
                deleted:
                  I18n.get.languageRepresentativeEducatorDiploma.messages
                    .languageRepresentativeEducatorDiplomaDeleted,
                fieldRequired: I18n.get.form.errors.required,
              }}
              operations={{
                category: "certLanguageRepresentativeEducatorDiploma",
                get: GET_LANGUAGE_REPRESENTATIVE_EDUCATOR_DIPLOMA,
                create: CREATE_LANGUAGE_REPRESENTATIVE_EDUCATOR_DIPLOMA,
                update: UPDATE_LANGUAGE_REPRESENTATIVE_EDUCATOR_DIPLOMA,
              }}
              paths={{
                back: "/diplom-sprakombudsutbildare",
              }}
              initialState={getInitialState()}
              renderPageTitle={(state) =>
                !isMultiple()
                  ? getPageTitle(
                      state,
                      I18n.get.languageRepresentativeEducatorDiploma.labels
                        .createLanguageRepresentativeEducatorDiploma,
                    )
                  : ""
              }
              urlParams={urlParams}
              createParam="skapa"
              classes={{
                toolbar: classes.toolbar,
                paper: classes.form,
              }}
              onFormChange={handleFormChange}
              onQueryLoading={(loading) =>
                formDispatch({ key: "formLoading", values: loading })
              }
              onDataChange={(data, fetchedData) => {
                dispatch({
                  values: {
                    ...data,
                    uid: fetchedData.certLanguageRepresentativeEducatorDiploma
                      .uid,
                  },
                });
              }}
              onComplete={handleComplete}
              onCancel={handleCancel}
              loadQueryOnParamsChange
              toolbarProps={{
                options: {
                  saveButton: {
                    label: isMultiple()
                      ? I18n.get.vocertActions.generate
                      : null,
                  },
                },
                extraActions: getExtraActions(),
              }}
            />
          </>
        }
        rightColumn={
          !formState.formLoading ? (
            <LanguageRepresentativeEducatorDiplomaPreview
              item={state}
              formState={formState.formState}
              id={id}
            />
          ) : (
            <></>
          )
        }
        rightColumnProps={{
          className: clsx(classes.previewWrapper),
        }}
      />
      {id === "skapa" && (
        <SaveConsentDialog context="LanguageRepresentativeEducatorDiploma" />
      )}
    </>
  );
};

export default LanguageRepresentativeEducatorDiplomaForm;
